/**
 * ImportSummary Component
 *
 * Displays validation summary statistics with icons and colors.
 * Shows download button for error report if validation errors exist.
 *
 * Features:
 * - Total, Valid, Invalid, and Ready-to-Import counts
 * - Color-coded statistics (green for valid, red for errors)
 * - Error CSV download button (conditional)
 * - Icons for visual clarity
 *
 * @module ImportExportTab/components/ImportSummary
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Button,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';

/**
 * ImportSummary Component
 *
 * @param {Object} props
 * @param {Object} props.validationResult - Result from csvValidator service
 * @returns {JSX.Element}
 */
function ImportSummary({ validationResult }) {
  const { summary, invalidRecords } = validationResult;

  /**
   * Generate error report CSV and trigger download
   */
  const handleDownloadErrorReport = () => {
    try {
      // Build CSV content
      const headers = ['Row #', 'Field', 'Error Message', 'Record Data'];
      const rows = [];

      invalidRecords.forEach(record => {
        record.errors.forEach(error => {
          // Create a simplified record data string
          const recordData = JSON.stringify(record)
            .replace(/"/g, '""'); // Escape quotes for CSV

          rows.push([
            record.rowNumber,
            error.field,
            error.message.replace(/"/g, '""'), // Escape quotes
            recordData
          ]);
        });
      });

      // Convert to CSV string
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Add UTF-8 BOM for Excel compatibility
      const csvWithBOM = '\uFEFF' + csvContent;

      // Create blob and download
      const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `import-errors-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading error report:', error);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h2">
            Import Summary
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          {/* Total Records */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" component="div" color="primary">
                {summary.totalRecords}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Records
              </Typography>
            </Box>
          </Grid>

          {/* Valid Records */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                <CheckCircleIcon sx={{ color: 'success.main', mr: 0.5 }} />
                <Typography variant="h4" component="div" color="success.main">
                  {summary.validCount}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Valid Records
              </Typography>
            </Box>
          </Grid>

          {/* Invalid Records */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                <ErrorIcon sx={{ color: 'error.main', mr: 0.5 }} />
                <Typography variant="h4" component="div" color="error.main">
                  {summary.invalidCount}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Invalid Records
              </Typography>
            </Box>
          </Grid>

          {/* Ready to Import */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" component="div" sx={{ color: '#7C3AED' }}>
                {summary.validCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ready to Import
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Error Download Button */}
        {summary.invalidCount > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadErrorReport}
              >
                Download Error Report
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

ImportSummary.propTypes = {
  validationResult: PropTypes.shape({
    validRecords: PropTypes.arrayOf(PropTypes.object).isRequired,
    invalidRecords: PropTypes.arrayOf(
      PropTypes.shape({
        rowNumber: PropTypes.number.isRequired,
        errors: PropTypes.arrayOf(
          PropTypes.shape({
            field: PropTypes.string.isRequired,
            message: PropTypes.string.isRequired
          })
        ).isRequired
      })
    ).isRequired,
    summary: PropTypes.shape({
      totalRecords: PropTypes.number.isRequired,
      validCount: PropTypes.number.isRequired,
      invalidCount: PropTypes.number.isRequired
    }).isRequired
  }).isRequired
};

export default ImportSummary;
